import { Texture } from 'pixi.js';

/**
 * Ники рисуются в canvas-текстуры и кэшируются по userId.
 *
 * В ТЗ (§11) стоял bitmap-шрифт, но ник в TikTok — произвольный юникод:
 * кириллица, арабский, CJK, эмодзи посреди строки. Фиксированный набор глифов
 * это не покрывает, будут квадраты. Canvas умеет всё, что умеет система,
 * а ник за матч не меняется — текстура готовится один раз на зрителя.
 */

const PAD = 8;
const MAX_CHARS = 16;

/** Сколько ждать перед уничтожением вытесненной текстуры. */
const GRACE_MS = 30_000;

export interface TextTex {
  texture: Texture;
  width: number;
  height: number;
}

export class TextCache {
  private readonly map = new Map<string, TextTex>();
  private readonly measure: CanvasRenderingContext2D;
  /**
   * Отложенное уничтожение — по той же причине, что и у аватарок: всплывающая
   * цифра урона держит текстуру почти секунду, к кэшу за ней не обращаясь.
   * Уничтоженная сразу, она роняла отрисовку с пустым source.
   */
  private readonly doomed: { tex: Texture; at: number }[] = [];

  constructor(private readonly limit = 900) {
    const c = document.createElement('canvas');
    this.measure = c.getContext('2d')!;
  }

  /** Белый текст с тёмной обводкой — красится тинтом под цвет команды. */
  get(text: string, size = 26, weight = '700'): TextTex {
    const clipped = clip(text);
    const key = `${clipped}|${size}|${weight}`;
    const have = this.map.get(key);
    if (have) {
      // Map хранит порядок вставки: перенос в конец за O(1).
      this.map.delete(key);
      this.map.set(key, have);
      return have;
    }

    const font = `${weight} ${size}px system-ui, "Noto Color Emoji", "Segoe UI Emoji", sans-serif`;
    this.measure.font = font;
    const w = Math.ceil(this.measure.measureText(clipped).width) + PAD * 2;
    const h = Math.ceil(size * 1.45) + PAD;

    const c = document.createElement('canvas');
    c.width = Math.max(2, w);
    c.height = Math.max(2, h);
    const ctx = c.getContext('2d')!;
    ctx.font = font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    ctx.lineWidth = Math.max(3, size * 0.22);
    ctx.strokeStyle = 'rgba(3,6,12,0.92)';
    ctx.strokeText(clipped, c.width / 2, c.height / 2);
    ctx.fillStyle = '#fff';
    ctx.fillText(clipped, c.width / 2, c.height / 2);

    const entry: TextTex = { texture: Texture.from(c), width: c.width, height: c.height };
    this.map.set(key, entry);
    this.evict();
    return entry;
  }

  /** Добивание отложенных. Вызывается рендером раз в кадр. */
  sweep(): void {
    const now = performance.now();
    while (this.doomed.length > 0 && this.doomed[0]!.at <= now) {
      this.doomed.shift()!.tex.destroy(true);
    }
  }

  private evict(): void {
    while (this.map.size > this.limit) {
      const oldest = this.map.keys().next();
      if (oldest.done) break;
      const e = this.map.get(oldest.value)!;
      this.map.delete(oldest.value);
      this.doomed.push({ tex: e.texture, at: performance.now() + GRACE_MS });
    }
  }

  get size(): number {
    return this.map.size;
  }
}

/** Длинные ники режем: подпись не должна перекрывать пол-арены. */
function clip(text: string): string {
  const chars = Array.from(text.trim());
  if (chars.length === 0) return '???';
  if (chars.length <= MAX_CHARS) return chars.join('');
  return chars.slice(0, MAX_CHARS - 1).join('') + '…';
}
